import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Business,
  Email,
  Phone,
  LocationOn,
  Language,
} from "@mui/icons-material";

import { useOrganizationDetails } from "../../hooks/queries/useOrganizationDetails";
import { updateSelectedInstitute, enterInstitute } from "../../store/slices/instituteContextSlice";

const DEFAULT_ORGANIZATION = {
  name: "",
  code: "",
  logo: null,
  contactName: "",
  email: "",
  phone: "",
  website: "",
  address: "",
  city: "",
  state: "",
  country: "",
  pincode: "",
  establishedYear: "",
  affiliatedTo: "",
  accreditation: "",
  description: "",
  socialLinks: {
    facebook: "",
    twitter: "",
    linkedin: "",
  },
  stats: { departments: 0, courses: 0, teachers: 0, students: 0 },
};

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatAddress = (org) => {
  const line1 = org.address;
  const line2 = [org.city, org.state].filter(Boolean).join(", ");
  const line3 = [org.country, org.pincode].filter(Boolean).join(" - ");
  const parts = [line1, line2, line3].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
};

const EMPTY_LABEL = "Not provided";

const buildOrganization = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { ...DEFAULT_ORGANIZATION };
  }

  const stats = payload?.stats || payload?.statistics || {};
  const institute = payload?.institute || {};
  const contact = payload?.instituteContact || payload?.contact || {};
  const derivedAddress =
    (typeof payload?.address === "object" && payload?.address !== null
      ? payload.address
      : null) ||
    (typeof institute?.address === "object" && institute?.address !== null
      ? institute.address
      : null) ||
    (institute?.street && {
      line1: `${institute.street}`,
      city: institute.city,
      state: institute.state,
      country: institute.country,
      postalCode: institute.pinCode,
    }) ||
    {};

  const addressLine =
    (typeof payload?.address === "string" && payload.address) ||
    (typeof institute?.address === "string" && institute.address) ||
    derivedAddress?.line1 ||
    derivedAddress?.addressLine1 ||
    derivedAddress?.street ||
    institute?.street ||
    "";

  const social = payload?.socialLinks || payload?.social || {};

  return {
    ...DEFAULT_ORGANIZATION,
    name:
      payload?.name ||
      institute?.name ||
      payload?.organizationName ||
      payload?.organisationName ||
      DEFAULT_ORGANIZATION.name,
    code:
      payload?.code ||
      institute?.instituteCode ||
      payload?.organizationCode ||
      payload?.organisationCode ||
      DEFAULT_ORGANIZATION.code,
    logo: payload?.logo || payload?.organizationLogo || DEFAULT_ORGANIZATION.logo,
    contactName:
      contact?.fullName ||
      payload?.contactName ||
      payload?.primaryContact ||
      DEFAULT_ORGANIZATION.contactName,
    email:
      payload?.email ||
      contact?.email ||
      payload?.contactEmail ||
      DEFAULT_ORGANIZATION.email,
    phone:
      payload?.phone ||
      contact?.phoneNumber ||
      contact?.phone ||
      payload?.contactNumber ||
      DEFAULT_ORGANIZATION.phone,
    website:
      payload?.website ||
      institute?.website ||
      contact?.website ||
      payload?.url ||
      DEFAULT_ORGANIZATION.website,
    address: addressLine,
    city: payload?.city || derivedAddress?.city || institute?.city || DEFAULT_ORGANIZATION.city,
    state:
      payload?.state ||
      derivedAddress?.state ||
      institute?.state ||
      DEFAULT_ORGANIZATION.state,
    country:
      payload?.country ||
      derivedAddress?.country ||
      institute?.country ||
      DEFAULT_ORGANIZATION.country,
    pincode:
      payload?.pincode ||
      derivedAddress?.postalCode ||
      derivedAddress?.zipCode ||
      institute?.pinCode ||
      DEFAULT_ORGANIZATION.pincode,
    establishedYear:
      payload?.establishedYear ||
      payload?.founded ||
      payload?.established ||
      DEFAULT_ORGANIZATION.establishedYear,
    affiliatedTo:
      payload?.affiliatedTo ||
      payload?.affiliation ||
      DEFAULT_ORGANIZATION.affiliatedTo,
    accreditation:
      payload?.accreditation ||
      payload?.accreditations ||
      DEFAULT_ORGANIZATION.accreditation,
    description:
      payload?.description ||
      payload?.about ||
      payload?.overview ||
      DEFAULT_ORGANIZATION.description,
    socialLinks: {
      ...DEFAULT_ORGANIZATION.socialLinks,
      facebook: social?.facebook || DEFAULT_ORGANIZATION.socialLinks.facebook,
      twitter: social?.twitter || DEFAULT_ORGANIZATION.socialLinks.twitter,
      linkedin:
        social?.linkedin ||
        social?.linkedIn ||
        DEFAULT_ORGANIZATION.socialLinks.linkedin,
    },
    stats: {
      ...DEFAULT_ORGANIZATION.stats,
      departments: toNumber(
        stats?.departments ||
          stats?.departmentCount ||
          payload?.departments ||
          payload?.totalDepartments,
        DEFAULT_ORGANIZATION.stats.departments
      ),
      courses: toNumber(
        stats?.courses ||
          stats?.courseCount ||
          payload?.courses ||
          payload?.totalCourses,
        DEFAULT_ORGANIZATION.stats.courses
      ),
      teachers: toNumber(
        stats?.teachers ||
          stats?.teacherCount ||
          payload?.teachers ||
          payload?.totalTeachers,
        DEFAULT_ORGANIZATION.stats.teachers
      ),
      students: toNumber(
        stats?.students ||
          stats?.studentCount ||
          payload?.students ||
          payload?.totalStudents,
        DEFAULT_ORGANIZATION.stats.students
      ),
    },
  };
};

const OrganizationDetailsSkeleton = () => (
  <Box>
    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
      <Box>
        <Skeleton variant="text" width={220} height={32} />
        <Skeleton variant="text" width={320} height={20} sx={{ mt: 0.5 }} />
      </Box>
    </Box>
    <Card sx={{ mb: 3, overflow: "hidden" }}>
      <CardContent>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
              <Skeleton variant="circular" width={100} height={100} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={40} />
                <Skeleton variant="rounded" width={80} height={24} sx={{ mt: 1 }} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={6} key={i}>
                  <Skeleton variant="rounded" height={56} />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width={160} height={28} />
            <Skeleton variant="text" width="100%" height={24} sx={{ mt: 2 }} />
            <Skeleton variant="text" width="90%" height={24} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="70%" height={24} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Skeleton variant="text" width={100} height={28} />
            <Skeleton variant="text" width="100%" height={24} sx={{ mt: 2 }} />
            <Skeleton variant="text" width="80%" height={24} sx={{ mt: 1 }} />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </Box>
);

const OrganizationDetails = () => {
  const dispatch = useDispatch();
  const { selectedInstitute, isInInstituteContext } = useSelector((state) => state.instituteContext || {});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const { data: payload, isLoading, isError } = useOrganizationDetails();

  // Sync sidebar "Managing Institute" with authoritative org/institute name from API
  useEffect(() => {
    if (!payload?.institute?.name) return;
    const institute = payload.institute;
    const update = { id: institute.id, name: institute.name, instituteCode: institute.instituteCode };
    // Only dispatch when data actually differs to avoid Redux update loop
    const isAlreadySynced =
      selectedInstitute?.id === institute.id &&
      selectedInstitute?.name === institute.name &&
      selectedInstitute?.instituteCode === institute.instituteCode;
    if (isAlreadySynced) return;

    if (selectedInstitute) {
      dispatch(updateSelectedInstitute(update));
    } else if (isInInstituteContext && institute.id) {
      dispatch(enterInstitute({ ...institute, ...update }));
    }
  }, [payload?.institute, selectedInstitute, isInInstituteContext, dispatch]);
  let organization = null;
  if (payload != null) {
    organization = buildOrganization(payload);
  } else if (isError) {
    organization = { ...DEFAULT_ORGANIZATION };
  }

  if (isLoading && payload == null) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading organization details...
        </Typography>
      </Box>
    );
  }

  if (organization == null) {
    return null;
  }

  const addressDisplay = formatAddress(organization);
  const stats = [
    { label: "Departments", value: organization.stats.departments },
    { label: "Courses", value: organization.stats.courses },
    { label: "Teachers", value: organization.stats.teachers },
    { label: "Students", value: organization.stats.students },
  ];

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ letterSpacing: "-0.02em" }}>
          Organization Details
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          View and manage your organization information
        </Typography>
      </Box>

      {/* Header Card */}
      <Card
        sx={{
          mb: 3,
          overflow: "hidden",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2c5282 50%, #1a365d 100%)",
          color: "white",
          boxShadow: "0 8px 24px rgba(30, 58, 95, 0.25)",
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
                <Avatar
                  sx={{
                    width: 88,
                    height: 88,
                    bgcolor: "rgba(255,255,255,0.15)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <Business sx={{ fontSize: 44 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
                    {organization.name || "Organization"}
                  </Typography>
                  {organization.code && (
                    <Chip
                      label={organization.code}
                      size="small"
                      sx={{
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "white",
                        fontWeight: 600,
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                  {organization.accreditation && (
                    <Chip
                      label={organization.accreditation}
                      size="small"
                      sx={{
                        bgcolor: "rgba(46, 125, 50, 0.9)",
                        color: "white",
                        ml: 1,
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Grid container spacing={1.5}>
                {stats.map((stat) => (
                  <Grid item xs={6} key={stat.label}>
                    <Box
                      sx={{
                        textAlign: "center",
                        bgcolor: "rgba(255,255,255,0.1)",
                        borderRadius: 2,
                        py: 2,
                        px: 1.5,
                        border: "1px solid rgba(255,255,255,0.08)",
                      }}
                    >
                      <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "text.primary" }}>
                Contact Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {organization.contactName && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: "primary.main", width: 36, height: 36 }}>
                    <Business sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Primary Contact
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {organization.contactName}
                    </Typography>
                  </Box>
                </Box>
              )}
              {[
                { icon: <Email sx={{ fontSize: 20 }} />, label: "Email", value: organization.email },
                { icon: <Phone sx={{ fontSize: 20 }} />, label: "Phone", value: organization.phone },
                { icon: <Language sx={{ fontSize: 20 }} />, label: "Website", value: organization.website },
              ].map((item) => (
                <Box
                  key={item.label}
                  sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.25 }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 2,
                      bgcolor: "grey.100",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "primary.main",
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary">
                      {item.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      sx={{
                        color: item.value ? "text.primary" : "text.secondary",
                        fontStyle: item.value ? "normal" : "italic",
                      }}
                    >
                      {item.value || EMPTY_LABEL}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Address */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, color: "text.primary" }}>
                Address
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: "grey.100",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "primary.main",
                    flexShrink: 0,
                  }}
                >
                  <LocationOn sx={{ fontSize: 20 }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      color: addressDisplay ? "text.primary" : "text.secondary",
                      fontStyle: addressDisplay ? "normal" : "italic",
                      lineHeight: 1.6,
                    }}
                  >
                    {addressDisplay || EMPTY_LABEL}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* About - Commented out per request
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                About
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {organization.description}
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Affiliated To
                </Typography>
                <Chip label={organization.affiliatedTo} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        */}
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setEditDialogOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Edit Organization Details
            <IconButton onClick={() => setEditDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Organization Name"
                defaultValue={organization.name}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Code"
                defaultValue={organization.code}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                defaultValue={organization.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                defaultValue={organization.phone}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Website"
                defaultValue={organization.website}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Established Year"
                defaultValue={organization.establishedYear}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                defaultValue={organization.address}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                defaultValue={organization.city}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                defaultValue={organization.state}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pincode"
                defaultValue={organization.pincode}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                defaultValue={organization.description}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationDetails;
